/**
 * KioskOrdering - Menu items view after category is selected
 * Shows items for the selected category with a back button to return to categories
 * Note: ItemCustomizationModal is now rendered at KioskView level for proper overlay positioning
 */
import { Coffee, ShoppingBag, Plus, X, Search, Eye, Crown, ChevronLeft, ShoppingCart, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useKiosk, BULACCINO_LOGO } from './KioskContext';

export default function KioskOrdering() {
  const {
    setView, isAccessible, buttonSizeClass, iconClass, iconButtonClass,
    settings, customer, search, setSearch, activeCategory, setActiveCategory,
    categoryTabs, filteredItems, cartCount, cartTotal,
    handleItemClick,
    accessibilityEnabled, setAccessibilityEnabled, resetKiosk
  } = useKiosk();

  // Get the active category label
  const activeCategoryLabel = categoryTabs.find(c => c.id === activeCategory)?.label || 'Menu';

  // Handle back button - go to category selection
  const handleBack = () => {
    console.log('Back button pressed - navigating to ordering (categories)');
    setView('ordering');
  };

  return (
    <div 
      className={`min-h-screen ${settings.high_contrast ? 'bg-black' : 'bg-gray-950'} text-white`} 
      style={{ 
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch',
        touchAction: 'pan-y'
      }}
      data-testid="kiosk-main"
    >
      {/* Cart Banner - Centered at top when items in cart */}
      {cartCount > 0 && (
        <div className="bg-primary/90 py-3 px-4">
          <button
            onClick={() => setView('cart')}
            className="w-full flex items-center justify-center gap-3 text-white touch-manipulation"
            style={{ touchAction: 'manipulation' }}
            data-testid="kiosk-view-cart-banner"
          >
            <ShoppingCart className="w-6 h-6" />
            <span className={`${isAccessible ? 'text-2xl' : 'text-xl'} font-bold`}>
              View Cart ({cartCount} items) - ${cartTotal.toFixed(2)}
            </span>
            <ChevronRight className="w-6 h-6" />
          </button>
        </div>
      )}

      {/* Header */}
      <div className={`sticky top-0 z-10 bg-gray-900 ${isAccessible ? 'p-3' : 'p-2'} flex items-center justify-between`}>
        <div className="flex items-center gap-2">
          {/* Back Button */}
          <button
            onClick={handleBack}
            onTouchEnd={(e) => {
              e.preventDefault();
              handleBack();
            }}
            className={`${isAccessible ? 'w-12 h-12' : 'w-10 h-10'} rounded-xl bg-gray-800 hover:bg-gray-700 active:bg-gray-600 flex items-center justify-center transition-colors touch-manipulation`}
            data-testid="kiosk-back-to-categories"
            style={{ touchAction: 'manipulation' }}
          >
            <ChevronLeft className={`${isAccessible ? 'w-7 h-7' : 'w-6 h-6'} text-white`} />
          </button>
          
          <div className="bg-white rounded-lg p-1">
            <img 
              src={BULACCINO_LOGO}
              alt="Bulaccino"
              className={isAccessible ? 'w-8 h-8' : 'w-7 h-7'}
            />
          </div>
          <div>
            <h1 className={`${isAccessible ? 'text-xl' : 'text-lg'} font-bold`}>{activeCategoryLabel}</h1>
            <p className={`${isAccessible ? 'text-xs' : 'text-[10px]'} text-gray-400`}>Select an item</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Start Over Button */}
          <Button
            variant="outline"
            onClick={resetKiosk}
            className={`${isAccessible ? 'px-3 py-1.5 text-xs' : 'px-2 py-1 text-[10px]'} rounded-lg border-red-600 text-red-400 hover:bg-red-600/20 touch-manipulation`}
            data-testid="kiosk-start-over-btn"
          >
            <X className="w-3 h-3 mr-1" />
            Start Over
          </Button>

          {/* Accessibility Toggle */}
          <Button
            variant="outline"
            onClick={() => setAccessibilityEnabled(!accessibilityEnabled)}
            className={`${isAccessible ? 'px-3 py-1.5' : 'px-2 py-1'} rounded-lg border-gray-600 ${accessibilityEnabled ? 'bg-primary text-white border-primary' : 'text-white'} touch-manipulation`}
            title="Toggle Accessibility Mode"
          >
            <Eye className="w-3 h-3" />
            <span className="ml-1 text-xs">A+</span>
          </Button>

          {/* Search */}
          <div className="relative">
            <Search className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search..."
              className={`pl-7 pr-2 ${isAccessible ? 'py-1.5 text-xs w-28' : 'py-1 text-[10px] w-24'} bg-gray-800 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary`}
              readOnly
              onClick={() => setSearch('')}
            />
          </div>
        </div>
      </div>

      {/* Categories - multi-row grid for portrait kiosk */}
      <div className={`px-3 ${isAccessible ? 'py-2' : 'py-1.5'} bg-gray-900/50`}>
        <div className="grid grid-cols-4 gap-1.5">
          {categoryTabs.map(cat => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={`${isAccessible ? 'px-2 py-2 text-xs' : 'px-2 py-1.5 text-[10px]'} rounded-lg font-medium text-center transition-all active:scale-[0.97] active:brightness-90 touch-manipulation ${
                activeCategory === cat.id
                  ? 'bg-primary text-white'
                  : 'bg-gray-800 text-gray-300 hover:bg-gray-700 active:bg-gray-600'
              }`}
              style={{ touchAction: 'manipulation' }}
            >
              {cat.label}
            </button>
          ))}
        </div>
      </div>

      {/* Menu Grid - Full width with touch scrolling */}
      <div 
        className={`${isAccessible ? 'p-3' : 'p-2'}`}
        style={{ 
          minHeight: 'calc(100vh - 200px)',
          touchAction: 'pan-y'
        }}
      >
        <div className={`grid grid-cols-3 ${isAccessible ? 'gap-3' : 'gap-2'}`}>
          {filteredItems.map(item => {
            const hasCustomizations = item.sizes?.length > 0 || item.modifiers?.length > 0 || item.ingredients?.length > 0;
            return (
              <button
                key={item.id}
                onClick={() => {
                  console.log('[KioskOrdering] Item clicked:', item.name, item.id);
                  handleItemClick(item);
                }}
                className={`bg-gray-900 ${isAccessible ? 'rounded-xl' : 'rounded-lg'} overflow-hidden text-left hover:ring-2 hover:ring-primary/50 transition-all active:scale-[0.97] active:brightness-90 touch-manipulation`}
                style={{ touchAction: 'manipulation' }}
                data-testid={`kiosk-item-${item.id}`}
              >
                <div className="relative">
                  {item.image ? (
                    <img 
                      src={item.image} 
                      alt={item.name}
                      className={`w-full ${isAccessible ? 'h-32' : 'h-28'} object-cover`}
                    />
                  ) : (
                    <div className={`w-full ${isAccessible ? 'h-32' : 'h-28'} bg-gray-800 flex items-center justify-center`}>
                      <Coffee className={isAccessible ? 'w-10 h-10 text-gray-600' : 'w-8 h-8 text-gray-600'} />
                    </div>
                  )}
                  {hasCustomizations && !item.skip_customization && !item.is_membership && !item.name?.toLowerCase().includes('coffee pass') && (
                    <span className={`absolute top-1 right-1 px-1.5 py-0.5 bg-blue-600 text-white text-[9px] font-medium rounded-full`}>
                      Customize
                    </span>
                  )}
                </div>
                <div className={isAccessible ? 'p-2' : 'p-1.5'}>
                  <h3 className={`${isAccessible ? 'text-sm' : 'text-xs'} font-medium mb-1 line-clamp-2`}>{item.name}</h3>
                  <div className="flex items-center justify-between">
                    {settings.show_prices && (
                      <span className={`${isAccessible ? 'text-base' : 'text-sm'} font-bold text-green-400`}>
                        ${item.price?.toFixed(2) || item.sizes?.[0]?.price?.toFixed(2)}
                      </span>
                    )}
                    <div className={`${isAccessible ? 'w-7 h-7' : 'w-6 h-6'} rounded-full bg-primary flex items-center justify-center ml-auto`}>
                      <Plus className={`${isAccessible ? 'w-4 h-4' : 'w-3 h-3'}`} />
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
        
        {/* Extra padding at bottom for scrolling */}
        <div className="h-24"></div>
      </div>

      {/* Customer Banner */}
      {customer && (
        <div className="fixed top-16 right-4 bg-amber-500/20 border border-amber-500/30 text-amber-200 px-3 py-1.5 rounded-xl flex items-center gap-2 z-40">
          <Crown className="w-4 h-4 text-amber-400" />
          <span className="font-medium text-sm">{customer.first_name}</span>
          <span className="text-amber-400 capitalize text-xs">({customer.loyalty_tier || 'Bronze'})</span>
        </div>
      )}

      {/* Accessibility Indicator */}
      {isAccessible && (
        <div className="fixed bottom-4 left-4 bg-primary/80 text-white px-3 py-1.5 rounded-lg text-xs flex items-center gap-2">
          <Eye className="w-3 h-3" />
          Accessibility
        </div>
      )}

      {/* ItemCustomizationModal is now rendered at KioskView level for proper overlay positioning */}
    </div>
  );
}
